import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/** Where hint preferences live. localStorage until task 4 adds `user_prefs` sync through the API. */
export interface HintStore {
  load(): { showHints: boolean; dismissed: string[] };
  save(state: { showHints: boolean; dismissed: string[] }): void;
}

const KEY = 'hoopoe.hints';
export const localHintStore: HintStore = {
  load() {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) ?? 'null');
      if (v && typeof v.showHints === 'boolean' && Array.isArray(v.dismissed)) return v;
    } catch {
      // unavailable or corrupt: fall through to defaults
    }
    return { showHints: true, dismissed: [] };
  },
  save(state) {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      // private mode: preferences last for this page only
    }
  },
};

interface HintsApi {
  showHints: boolean;
  setShowHints(v: boolean): void;
  isDismissed(id: string): boolean;
  dismiss(id: string): void;
  restore(id: string): void;
  resetAll(): void;
}

const HintsContext = createContext<HintsApi | null>(null);

/** Browser copy for instant start-up, synced with the signed-in user's prefs on the server. */
export const serverHintStore: HintStore & { hydrate(apply: (s: { showHints: boolean; dismissed: string[] }) => void): void } = {
  load: localHintStore.load,
  save(state) {
    localHintStore.save(state);
    fetch('/api/me/prefs', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(state) }).catch(() => {});
  },
  hydrate(apply) {
    fetch('/api/me/prefs')
      .then(r => (r.ok ? r.json() : null))
      .then((s: { showHints: boolean; dismissed: string[] } | null) => {
        if (!s) return;
        const local = localHintStore.load();
        // Union: something dismissed on any device stays dismissed.
        const merged = { showHints: s.showHints, dismissed: [...new Set([...s.dismissed, ...local.dismissed])] };
        localHintStore.save(merged);
        apply(merged);
      })
      .catch(() => {});
  },
};

export function HintsProvider({ children, store = localHintStore }: { children: ReactNode; store?: HintStore }) {
  const [state, setState] = useState(() => store.load());
  useEffect(() => {
    if ('hydrate' in store) (store as typeof serverHintStore).hydrate(setState);
  }, [store]);
  const update = useCallback(
    (fn: (s: typeof state) => typeof state) =>
      setState(prev => {
        const next = fn(prev);
        store.save(next);
        return next;
      }),
    [store],
  );
  const api = useMemo<HintsApi>(
    () => ({
      showHints: state.showHints,
      setShowHints: v => update(s => ({ ...s, showHints: v })),
      isDismissed: id => state.dismissed.includes(id),
      dismiss: id => update(s => (s.dismissed.includes(id) ? s : { ...s, dismissed: [...s.dismissed, id] })),
      restore: id => update(s => ({ ...s, dismissed: s.dismissed.filter(d => d !== id) })),
      resetAll: () => update(s => ({ ...s, dismissed: [] })),
    }),
    [state, update],
  );
  return <HintsContext.Provider value={api}>{children}</HintsContext.Provider>;
}

export function useHints(): HintsApi {
  const ctx = useContext(HintsContext);
  if (!ctx) throw new Error('useHints needs <HintsProvider>');
  return ctx;
}
